import { withStyles, WithStyles, LinearProgress, Grid } from '@material-ui/core'
import i18n from 'i18next'
import { path } from 'ramda'
import { withTranslation, WithTranslation } from 'react-i18next'
import { connect } from 'react-redux'
import { Dispatch } from 'redux'
import {
  addApartmentMedia,
  confirmMedia,
  deleteApartmentMedia,
  reorderApartmentMedia,
  stepBack,
  stepClick,
  stepForward,
  updateApartmentClientInformation,
  updateApartmentConfirmation,
  updateApartmentInformation,
  updateApartmentClientInformationToOverview
} from '../../../actions/apartments'
import { navigate } from '../../../actions/routes'
import {
  AddApartmentMedia,
  Navigate,
  UpdateApartmentClientInformation,
  UpdateApartmentConfirmation,
  UpdateApartmentInformation,
  UpdateApartmentClientInformationToOverview
} from '../../../actions/types'
import Container from '../../../components/Container'
import {
  selectApartmentById,
  selectApartmentFilesByApartmentId
} from '../../../selectors/apartments'
import {
  selectContentIdFromPathname,
  selectUserIdFromPathname
} from '../../../selectors/router'
import {
  selectIsAdminUser,
  selectAreUsersLoading,
  selectUserByUserId,
  selectUserAttributes,
  selectIsMobile
} from '../../../selectors/users'
import { UserAttributes } from '../../../services/auth/types'
import { BucketObject } from '../../../services/storage/types'
import { apartmentStyles } from '../../../styles'
import { Apartment, ClientContactInfo } from '../../../types/apartments'
import { PathValue } from '../../../types/returnTypes'
import { StateInterface } from '../../../types/states'
import { User } from '../../../types/users'
import { getClientContactInfo } from '../../../utils/apartments'
import { bindSubmitActionToPromise } from '../../../utils/forms'
import NewApplicationProgress from '../../NewApplication/Progress'
import { Steps } from './Steps'

type ParamsProps = {
  step: number
}

interface MatchProps {
  params: ParamsProps
}

interface StepperProps extends WithTranslation, WithStyles {
  apartment?: Apartment
  user: User | undefined
  clientContactInfo: ClientContactInfo | undefined
  areUsersLoading: boolean
  doNavigate: Navigate
  doStepBack: () => void
  doStepForward: () => void
  doConfirmMedia: () => void
  updateApartmentClientInfo: UpdateApartmentClientInformation
  updateApartmentInfo: UpdateApartmentInformation
  updateApartmentConfirmation: UpdateApartmentConfirmation
  updateApartmentClientInfoToOverview: UpdateApartmentClientInformationToOverview
  addApartmentMediaFiles: AddApartmentMedia
  apartmentConfirmation: (arg: any) => any
  deleteMedia: (arg: any) => any
  reorderMedia: (arg: any) => any
  files: BucketObject[]
  match: MatchProps
  isAdmin: boolean
  doStepClick: (step: number) => any
  t: i18n.TFunction
  form: any
  isMobile: any
}

const Stepper = ({
  t,
  apartment,
  classes,
  clientContactInfo,
  areUsersLoading,
  updateApartmentClientInfo,
  updateApartmentClientInfoToOverview,
  updateApartmentInfo,
  apartmentConfirmation,
  addApartmentMediaFiles,
  deleteMedia,
  reorderMedia,
  doStepBack,
  doStepForward,
  doConfirmMedia,
  files,
  doNavigate,
  isAdmin,
  doStepClick,
  form,
  isMobile,
  match: {
    params: { step }
  }
}: StepperProps) => {
  if (!apartment) {
    return <LinearProgress />
  }
  const currentStep = Number(step)
  const isConfirmed: PathValue<boolean> = path(
    ['metadata', 'contract_confirmed'],
    apartment
  )
  const baseFormStepProps = {
    currentStep,
    apartment,
    isAdmin,
    isConfirmed,
    doStepBack,
    isMobile,
    doStepForward
  }
  const stepProps = {
    baseFormStepProps,
    classes,
    updateApartmentInfo,
    updateApartmentClientInfo,
    updateApartmentClientInfoToOverview,
    clientContactInfo,
    areUsersLoading,
    apartmentConfirmation,
    addApartmentMediaFiles,
    deleteMedia,
    reorderMedia,
    doConfirmMedia,
    files,
    doNavigate,
    currentStep,
    apartment,
    isMobile
  }
  return (
    <Container
      styles={{ maxWidth: '1131px', margin: 'auto', padding: '8px 20px' }}
    >
      <NewApplicationProgress
        progress={currentStep === 0 ? 30 : ((currentStep + 1) / 14) * 70 + 30}
      />
      <Grid container>
        <Grid item xs={12}>
          <Steps stepProps={stepProps} />
        </Grid>
      </Grid>
    </Container>
  )
}

const mapStateToProps = (state: StateInterface) => {
  const apartmentId = selectContentIdFromPathname(state)
  const userId = selectUserIdFromPathname(state)
  const userAttributes = selectUserAttributes(state)
  const user = userId ? selectUserByUserId(userId)(state) : undefined
  const apartment = apartmentId
    ? selectApartmentById(apartmentId)(state)
    : undefined
  const clientContactInfo =
    Object.values(userAttributes || {}).length > 0
      ? getClientContactInfo({
          apartment,
          user,
          userAttributes: userAttributes as UserAttributes | undefined
        })
      : undefined
  return {
    apartment,
    isMobile: selectIsMobile(state),
    areUsersLoading: selectAreUsersLoading(state),
    clientContactInfo,
    files: apartmentId
      ? selectApartmentFilesByApartmentId(apartmentId)(state)
      : undefined,
    isAdmin: selectIsAdminUser(state)
  }
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updateApartmentClientInfo: bindSubmitActionToPromise(
    dispatch,
    updateApartmentClientInformation
  ),
  updateApartmentClientInfoToOverview: bindSubmitActionToPromise(
    dispatch,
    updateApartmentClientInformationToOverview
  ),
  updateApartmentInfo: bindSubmitActionToPromise(
    dispatch,
    updateApartmentInformation
  ),
  apartmentConfirmation: bindSubmitActionToPromise(
    dispatch,
    updateApartmentConfirmation
  ),
  addApartmentMediaFiles: bindSubmitActionToPromise(
    dispatch,
    addApartmentMedia
  ),
  deleteMedia: (fileKey: string) => dispatch(deleteApartmentMedia(fileKey)),
  reorderMedia: ({
    oldIndex,
    newIndex
  }: {
    oldIndex: number
    newIndex: number
  }) => dispatch(reorderApartmentMedia({ oldIndex, newIndex })),
  doConfirmMedia: () => dispatch(confirmMedia()),
  doStepBack: () => dispatch(stepBack()),
  doStepForward: () => dispatch(stepForward()),
  doNavigate: (toPath: string) => dispatch(navigate(toPath)),
  doStepClick: (step: number) => dispatch(stepClick({ step }))
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(apartmentStyles)(withTranslation()(Stepper as any)))
